import React, { useCallback, useEffect, useState } from 'react';
import { Check, Copy, Link2, Loader2, LogOut, RefreshCw, SlidersHorizontal, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  crearLinkEquipo,
  desactivarLinkEquipo,
  EquipoError,
  fetchEquipo,
  guardarPermisosDeMiembro,
  sacarMiembro,
  salirDelEquipo,
  urlDeInvitacion,
  type Equipo as EquipoData,
  type MiembroEquipo,
} from '../../lib/equipoApi';
import { ModalConfirmar } from '../../components/ModalConfirmar';
import { CACHE_KEYS, invalidateCache } from '../../utils/sessionCache';
import { puedeInvitarEquipo } from '../../lib/planCapabilities';
import { PermisosDelMiembro, resumenDePermisos } from './PermisosDelMiembro';

interface EquipoProps {
  /** `company_profiles.plan`; Settings ya lo baja. */
  plan: string;
  /** `profiles.id` del usuario en sesión, para marcar "(vos)" en la lista. */
  miId: string;
  setError: (error: string | null) => void;
  setSuccess: (success: string | null) => void;
}

/**
 * Sección "Equipo" de Configuración — spec 2026-09-11-invitaciones-equipo §4.1.
 * El dueño ve el link (copiar / regenerar / desactivar) y saca gente; el miembro ve la
 * lista y puede salir. Sin card propia: el recuadro blanco lo pone Settings.
 */
const fecha = (iso: string) => new Date(iso).toLocaleDateString('es-AR', { day: 'numeric', month: 'numeric' });

const BOTON =
  'inline-flex items-center gap-1.5 py-2 px-4 rounded-md text-[14px] font-medium transition-colors disabled:opacity-60';
const PRIMARIO = `${BOTON} bg-brand-primary text-white hover:opacity-90`;
const SECUNDARIO = `${BOTON} bg-white border border-neutral-200 text-brand-black/90 hover:bg-neutral-50`;

/**
 * "2 de 3 personas"; sin tope (Corporativo) o sin plan de equipo (un Ultra con su equipo de
 * antes), sólo cuántas son: "4 de 1" parecería un error.
 */
export function textoDePersonas(p: { miembros: number; tope: number | null; sinEquipo: boolean }): string {
  const cuantas = `${p.miembros} ${p.miembros === 1 ? 'persona' : 'personas'}`;
  if (p.tope === null || p.sinEquipo) return cuantas;
  return `${p.miembros} de ${p.tope} personas`;
}

export const Equipo: React.FC<EquipoProps> = ({ plan, miId, setError, setSuccess }) => {
  const navigate = useNavigate();
  const [equipo, setEquipo] = useState<EquipoData | null>(null);
  const [cargando, setCargando] = useState(true);
  const [trabajando, setTrabajando] = useState(false);
  const [copiado, setCopiado] = useState(false);
  /** El miembro cuyos permisos está editando el dueño (ADR-2026-10-19-permisos-por-miembro-del-equipo). */
  const [editando, setEditando] = useState<string | null>(null);
  /** A quién se está por sacar: abre el modal de confirmación (antes era `window.confirm`). */
  const [aSacar, setASacar] = useState<MiembroEquipo | null>(null);
  // Sólo los planes de equipo invitan (ADR-2026-10-27-planes-de-equipo-en-el-front); el resto
  // ve el bloque con el CTA. Manda lo que dice el backend; si todavía no lo manda, el plan.
  const sinEquipo = equipo?.incluyeEquipo != null ? !equipo.incluyeEquipo : !puedeInvitarEquipo(plan);
  // Con lugar para sumar: sin tope (Corporativo) o con menos personas que el tope del plan.
  const equipoCompleto = !!equipo && equipo.tope !== null && equipo.miembros.length >= equipo.tope;

  const [fallo, setFallo] = useState(false);
  const cargar = useCallback(async () => {
    try {
      setEquipo(await fetchEquipo());
      setFallo(false);
    } catch {
      setError('No pudimos cargar tu equipo. Probá de nuevo.');
      setFallo(true);
    } finally {
      setCargando(false);
    }
  }, [setError]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const correr = async (accion: () => Promise<void>, exito: string) => {
    setTrabajando(true);
    setError(null);
    setSuccess(null);
    try {
      await accion();
      await cargar();
      setSuccess(exito);
    } catch (e) {
      setError(e instanceof EquipoError ? e.message : 'No se pudo completar. Probá de nuevo.');
    } finally {
      setTrabajando(false);
    }
  };

  const copiar = async () => {
    if (!equipo?.link) return;
    try {
      await navigator.clipboard.writeText(urlDeInvitacion(equipo.link.token));
      setCopiado(true);
      window.setTimeout(() => setCopiado(false), 1500);
    } catch {
      setError('No se pudo copiar. Seleccioná el link y copialo a mano.');
    }
  };

  // Sin lugar para invitar, el link que ya se compartió sigue vivo: si se libera un lugar (o pasa
  // a un plan de equipo), alguien puede entrar con él. Apagarlo no depende del plan (review
  // Greptile #211).
  const linkVigenteSinLugar =
    equipo?.link && equipo.link.estado === 'vigente' ? (
      <div className="mt-4 pt-3 border-t border-neutral-200">
        <p className="text-[13px] text-neutral-500">
          Tu link de invitación sigue activo hasta el {fecha(equipo.link.expiraAt)}.{' '}
          {sinEquipo
            ? 'Si pasás a un plan de equipo, alguien podría entrar con él.'
            : 'Si se libera un lugar, alguien podría entrar con él.'}
        </p>
        <button
          type="button"
          disabled={trabajando}
          className={`${SECUNDARIO} mt-2`}
          onClick={() => correr(desactivarLinkEquipo, 'Link desactivado.')}
        >
          Desactivar
        </button>
      </div>
    ) : null;

  const salir = async () => {
    if (!window.confirm('Vas a dejar de ver esta cuenta. Vas a poder crear tu propia cuenta. ¿Salir del equipo?')) {
      return;
    }
    await correr(async () => {
      await salirDelEquipo();
      // Lo que el front cacheó de la cuenta ya miente (spec §3.2).
      invalidateCache(CACHE_KEYS.USER_PROFILE);
      invalidateCache(CACHE_KEYS.COMPANY_DATA);
      try {
        localStorage.removeItem('atlas-user-plan');
        localStorage.removeItem('atlas-user-group');
      } catch {
        /* noop */
      }
      window.location.assign('/');
    }, 'Saliste del equipo.');
  };

  return (
    <div>
      <div className="pb-5 mb-5 border-b border-neutral-200">
        <h2 className="text-[17px] font-semibold text-brand-black/90">Equipo</h2>
        <p className="text-[13px] text-neutral-500 mt-0.5">Invitá a tu equipo y elegí qué puede hacer cada uno</p>
      </div>

      {!equipo && fallo ? (
        // Sin esto, un error dejaba el spinner girando para siempre debajo del aviso.
        <div className="flex justify-center py-8">
          <button
            type="button"
            className={SECUNDARIO}
            onClick={() => {
              setError(null);
              setCargando(true);
              void cargar();
            }}
          >
            <RefreshCw className="w-4 h-4" /> Reintentar
          </button>
        </div>
      ) : cargando || !equipo ? (
        <div className="flex justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-brand-primary" />
        </div>
      ) : (
        <div className="space-y-6">
          {equipo.soyDueno && (
            <section className="p-4 bg-surface-elevated border border-neutral-200 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <Link2 className="w-4 h-4 text-neutral-500" />
                <h3 className="text-[15px] font-medium text-brand-black/90">Link de invitación</h3>
              </div>
              {sinEquipo ? (
                <>
                  <p className="text-[14px] text-neutral-600">
                    {equipo.miembros.length > 1
                      ? 'Tu equipo sigue como está. Para sumar más personas, pasate a un plan de equipo: Equipos, Agencias o Corporativo.'
                      : 'Invitar a tu equipo es de los planes Equipos, Agencias y Corporativo.'}
                  </p>
                  <button type="button" onClick={() => navigate('/subscription?equipos=1')} className={`${PRIMARIO} mt-3`}>
                    Ver planes
                  </button>
                  {linkVigenteSinLugar}
                </>
              ) : equipoCompleto ? (
                <>
                  <p className="text-[14px] text-neutral-600">
                    Tu equipo está completo: tu plan incluye {equipo.tope} personas. Para sumar más, pasate a un plan
                    con más lugar.
                  </p>
                  <button type="button" onClick={() => navigate('/subscription?equipos=1')} className={`${PRIMARIO} mt-3`}>
                    Ver planes
                  </button>
                  {linkVigenteSinLugar}
                </>
              ) : equipo.link && equipo.link.estado === 'vigente' ? (
                <>
                  <div className="flex gap-2">
                    <input
                      readOnly
                      value={urlDeInvitacion(equipo.link.token)}
                      onFocus={(e) => e.currentTarget.select()}
                      className="flex-1 min-w-0 px-3 py-2 text-[14px] text-brand-black/90 bg-white border border-neutral-200 rounded-md truncate"
                    />
                    <button type="button" onClick={copiar} className={PRIMARIO}>
                      {copiado ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      {copiado ? 'Copiado' : 'Copiar'}
                    </button>
                  </div>
                  <p className="text-[13px] text-neutral-500 mt-2">
                    Vence el {fecha(equipo.link.expiraAt)}
                    {equipo.link.usos > 0 &&
                      ` · ${equipo.link.usos} ${equipo.link.usos === 1 ? 'persona entró' : 'personas entraron'} con este link`}
                  </p>
                  <div className="flex gap-2 mt-3">
                    <button
                      type="button"
                      disabled={trabajando}
                      className={SECUNDARIO}
                      onClick={() =>
                        window.confirm('El link anterior deja de servir. ¿Regenerar?') &&
                        correr(async () => {
                          await crearLinkEquipo();
                        }, 'Link nuevo listo.')
                      }
                    >
                      <RefreshCw className="w-4 h-4" /> Regenerar
                    </button>
                    <button
                      type="button"
                      disabled={trabajando}
                      className={SECUNDARIO}
                      onClick={() => correr(desactivarLinkEquipo, 'Link desactivado.')}
                    >
                      Desactivar
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-[14px] text-neutral-600">
                    {equipo.link ? `Tu link anterior venció el ${fecha(equipo.link.expiraAt)}. ` : ''}
                    Creá un link y mandáselo a tu equipo por WhatsApp o mail.
                  </p>
                  <button
                    type="button"
                    disabled={trabajando}
                    className={`${PRIMARIO} mt-3`}
                    onClick={() =>
                      correr(async () => {
                        await crearLinkEquipo();
                      }, 'Link listo. Copialo y mandáselo a tu equipo.')
                    }
                  >
                    {trabajando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                    Crear link
                  </button>
                </>
              )}
            </section>
          )}

          <section>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-neutral-500" />
                <h3 className="text-[15px] font-medium text-brand-black/90">Miembros</h3>
              </div>
              <span className="text-[13px] text-neutral-500">
                {textoDePersonas({ miembros: equipo.miembros.length, tope: equipo.tope, sinEquipo })}
              </span>
            </div>
            <ul className="divide-y divide-neutral-200 border border-neutral-200 rounded-lg overflow-hidden">
              {equipo.miembros.map((m) => {
                // `marcas` viene sólo para el dueño anotado: es quien puede cambiar permisos.
                const puedeEditar = equipo.marcas !== null && !m.esDueno;
                const resumen = m.esDueno ? null : resumenDePermisos(m.permisos, equipo.marcas?.length ?? null);
                return (
                  <li key={m.id} className="bg-white">
                    <div className="flex items-center gap-3 px-3 py-2.5">
                      <div className="w-8 h-8 rounded-full bg-neutral-100 text-neutral-600 flex items-center justify-center text-[13px] font-medium shrink-0">
                        {m.nombre.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[14px] font-medium text-brand-black/90 truncate">
                          {m.nombre}
                          {m.id === miId && <span className="text-neutral-400 font-normal"> (vos)</span>}
                        </p>
                        <p className="text-[12px] text-neutral-500 truncate">{m.email ?? ''}</p>
                        {resumen && <p className="text-[12px] text-neutral-500 truncate">{resumen}</p>}
                      </div>
                      <span
                        className={`text-[11px] px-2 py-0.5 rounded-full ${
                          m.esDueno ? 'bg-[rgb(var(--brand-primary-rgb)_/_0.085)] text-brand-primary' : 'bg-neutral-100 text-neutral-600'
                        }`}
                      >
                        {m.esDueno ? 'Administrador' : 'Miembro'}
                      </span>
                      {puedeEditar && (
                        <button
                          type="button"
                          disabled={trabajando}
                          aria-expanded={editando === m.id}
                          className={`inline-flex items-center gap-1.5 py-1.5 px-3 rounded-md text-[13px] font-medium border transition-colors ${
                            editando === m.id
                              ? 'border-brand-primary text-brand-primary bg-white'
                              : 'border-neutral-200 text-brand-black/90 bg-white hover:bg-neutral-50'
                          }`}
                          onClick={() => setEditando((actual) => (actual === m.id ? null : m.id))}
                        >
                          <SlidersHorizontal className="w-4 h-4" /> Permisos
                        </button>
                      )}
                    </div>
                    {puedeEditar && editando === m.id && (
                      <PermisosDelMiembro
                        nombre={m.nombre}
                        inicial={m.permisos}
                        marcas={equipo.marcas ?? []}
                        guardando={trabajando}
                        onCancelar={() => setEditando(null)}
                        onSacar={() => setASacar(m)}
                        onGuardar={(p) =>
                          correr(async () => {
                            await guardarPermisosDeMiembro(m.id, p);
                            setEditando(null);
                          }, `Listo, guardamos los permisos de ${m.nombre}.`)
                        }
                      />
                    )}
                  </li>
                );
              })}
            </ul>
          </section>

          <ModalConfirmar
            abierto={aSacar !== null}
            titulo={aSacar ? `¿Sacar a ${aSacar.nombre} del equipo?` : ''}
            detalle="Deja de ver esta cuenta. Lo que creó se queda acá."
            confirmar="Sacar del equipo"
            trabajando={trabajando}
            onCancelar={() => setASacar(null)}
            onConfirmar={async () => {
              if (!aSacar) return;
              const quien = aSacar;
              await correr(() => sacarMiembro(quien.id), `${quien.nombre} ya no es parte del equipo.`);
              setASacar(null);
              setEditando(null);
            }}
          />

          {!equipo.soyDueno && (
            <button type="button" disabled={trabajando} onClick={salir} className={`${SECUNDARIO} text-red-600`}>
              <LogOut className="w-4 h-4" /> Salir del equipo
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default Equipo;
